import {
  Supertype,
  supertypeClass,
  property,
  remote,
  Remoteable,
  Persistable,
  amorphicStatic,
} from '@haventech/amorphic';
import {
  IsomorphicPersistent,
  Persistent,
  IsomorphicQuery,
  Query,
  Isomorphic,
  Serializable,
  AppController,
  SubController,
  Everything,
} from '@hit/amorphic-types';

@supertypeClass({ toClient: false, toServer: true })
export class TwoFactorAuth extends Everything {
  static TIMEOUT = 5 * 60 * 1000; // 5 minutes, time before code expires
  static LOCKOUT = 2 * 60 * 60 * 1000; //2 hours, time an account should be locked for
  static MAX_ATTEMPTS = 3;
  static EXPIRED: string = 'expired';
  static LOCKED: string = 'locked';
  static VERIFIED: string = 'verified';
  static INVALID: string = 'invalid';
  static OPEN: string = 'open';
  static SUCCESS: string = 'success';
  static PENDING: string = 'pending';

  @property({ values: ['open', 'verified', 'invalid', 'expired', 'locked'] })
  private _status: string;

  @property({ rule: ['telephone'] })
  private _phone: string;

  @property({ rule: ['date'] })
  private _timeSent: Date;

  @property({ rule: ['date'] })
  private _timeLockedOut: Date;

  @property()
  private _numberInvalidAttempts: number;

  @property()
  private _code: number;

  constructor() {
    super();
  }

  static new() {
    return new TwoFactorAuth().init();
  }

  init() {
    return this;
  }

  getPhone(): string {
    return this._phone;
  }

  setPhone(phone: string): void {
    this._phone = phone;
  }

  getCode(): number {
    return this._code;
  }

  setCode(code: number): void {
    this._code = code;
  }

  getStatus(): string {
    return this._status;
  }

  setStatus(status: string): void {
    this._status = status;
  }

  getNumberInvalidAttempts(): number {
    return this._numberInvalidAttempts;
  }

  setNumberInvalidAttempts(numberInvalidAttempts: number): void {
    this._numberInvalidAttempts = numberInvalidAttempts;
  }

  getTimeSent(): Date {
    return this._timeSent;
  }

  setTimeSent(timeSent: Date) {
    this._timeSent = timeSent;
  }

  getTimeLockedOut(): Date {
    return this._timeLockedOut;
  }

  setTimeLockedOut(timeLockedOut: Date): void {
    this._timeLockedOut = timeLockedOut;
  }

  incrementInvalidAttempts(): void {
    this._numberInvalidAttempts++;
  }

  // TODO remove all of the getter/setters below once angular coverpath is live
  get phone() {
    return this._phone;
  }

  set phone(phone: string) {
    this._phone = phone;
  }

  get code() {
    if (this._code && Date.now() - this._timeSent.getTime() > TwoFactorAuth.TIMEOUT) {
      this._status = TwoFactorAuth.EXPIRED;
    } else {
      return this._code;
    }
  }

  //set new code, this sets status to SENT and numberInvalidAttempts to 0
  set code(code: number) {
    this._code = code;
    this._numberInvalidAttempts = 0;
    this._status = TwoFactorAuth.OPEN;
    this._timeSent = new Date();
  }

  get status() {
    if (this._status === TwoFactorAuth.LOCKED && Date.now() - this._timeLockedOut.getTime() > TwoFactorAuth.LOCKOUT) {
      this._status = TwoFactorAuth.OPEN;
    }
    if (this._status === TwoFactorAuth.OPEN && Date.now() - this._timeSent.getTime() > TwoFactorAuth.TIMEOUT) {
      this._status = TwoFactorAuth.EXPIRED;
      return TwoFactorAuth.EXPIRED;
    } else {
      return this._status;
    }
  }

  set status(status: string) {
    this._status = status;
  }

  get numberInvalidAttempts() {
    return this._numberInvalidAttempts;
  }

  incrementNumberInvalidAttempts() {
    this._numberInvalidAttempts++;
    if (this._numberInvalidAttempts >= TwoFactorAuth.MAX_ATTEMPTS) {
      this._status = TwoFactorAuth.LOCKED;
      this._timeLockedOut = new Date();
    }
  }
}
